/**
 * Onboarding screening and applicability suggestions of the fiscal obligations (E01, ALC-4767).
 *
 * The accountant answers a fixed list of questions about the facts that change which obligations a
 * company owes. The ERP turns the answers, the resolved Taxpayer profile (IRPJ regime, CBS and IBS
 * treatments) and the catalog into a suggestion per obligation, with the reason. A suggestion is
 * never a decision: the accountant accepts, changes or refuses it.
 */
import type {
  FiscalObligationCatalogItem,
  ObligationApplicabilityDecision,
  ObligationReadOnlyReason,
} from './fiscal-obligations.js';
import type {
  CompanyTaxRegime,
  ConsumptionTaxTreatment,
  ObligationApplicabilityState,
  ObligationScopeType,
  ObligationScreeningAnswer,
} from './generated/enums.js';

/** Who a question is about: the company (answered once on the head office) or one establishment. */
export type ScreeningQuestionLevel = 'TAXPAYER' | 'ENTITY';

export const SCREENING_QUESTION_GROUPS = [
  'PAYROLL_AND_WITHHOLDINGS',
  'SERVICES_ABROAD',
  'GOODS_AND_STATE_TAXES',
  'MUNICIPAL',
  'SPECIAL_REGIME',
  'CORPORATE_EVENT',
] as const;
export type ScreeningQuestionGroup = (typeof SCREENING_QUESTION_GROUPS)[number];

/**
 * The screening questions, in the order they are asked. The wording lives in the web i18n files
 * (keyed by `code`); the code is what is stored with the answer.
 */
export const FISCAL_OBLIGATION_SCREENING_QUESTIONS = [
  { code: 'HAS_PAYROLL', level: 'TAXPAYER', group: 'PAYROLL_AND_WITHHOLDINGS' },
  { code: 'MAKES_WITHHOLDINGS', level: 'TAXPAYER', group: 'PAYROLL_AND_WITHHOLDINGS' },
  { code: 'PAYS_DIVIDENDS', level: 'TAXPAYER', group: 'PAYROLL_AND_WITHHOLDINGS' },
  { code: 'IMPORTS_SERVICES', level: 'TAXPAYER', group: 'SERVICES_ABROAD' },
  { code: 'IMPORTS_SAAS_CLOUD', level: 'TAXPAYER', group: 'SERVICES_ABROAD' },
  { code: 'EXPORTS_SERVICES', level: 'TAXPAYER', group: 'SERVICES_ABROAD' },
  { code: 'EXPORTS_SAAS_CLOUD', level: 'TAXPAYER', group: 'SERVICES_ABROAD' },
  { code: 'SELLS_GOODS', level: 'TAXPAYER', group: 'GOODS_AND_STATE_TAXES' },
  { code: 'HAS_ICMS_IPI_REGISTRATION', level: 'TAXPAYER', group: 'GOODS_AND_STATE_TAXES' },
  { code: 'ISS_DF', level: 'ENTITY', group: 'MUNICIPAL' },
  { code: 'SPECIAL_IMMUNE_OR_EXEMPT', level: 'TAXPAYER', group: 'SPECIAL_REGIME' },
  { code: 'SPECIAL_COOPERATIVE', level: 'TAXPAYER', group: 'SPECIAL_REGIME' },
  { code: 'SPECIAL_FINANCIAL_INSTITUTION', level: 'TAXPAYER', group: 'SPECIAL_REGIME' },
  { code: 'EVENT_EXTINCTION', level: 'TAXPAYER', group: 'CORPORATE_EVENT' },
  { code: 'EVENT_SPLIT', level: 'TAXPAYER', group: 'CORPORATE_EVENT' },
  { code: 'EVENT_MERGER', level: 'TAXPAYER', group: 'CORPORATE_EVENT' },
  { code: 'EVENT_INCORPORATION', level: 'TAXPAYER', group: 'CORPORATE_EVENT' },
] as const satisfies readonly {
  code: string;
  level: ScreeningQuestionLevel;
  group: ScreeningQuestionGroup;
}[];

export type ScreeningQuestionCode = (typeof FISCAL_OBLIGATION_SCREENING_QUESTIONS)[number]['code'];

export const SCREENING_QUESTION_CODES = FISCAL_OBLIGATION_SCREENING_QUESTIONS.map(
  (question) => question.code,
) as readonly ScreeningQuestionCode[];

export function isScreeningQuestionCode(value: string): value is ScreeningQuestionCode {
  return (SCREENING_QUESTION_CODES as readonly string[]).includes(value);
}

/** The question codes of each level, in the order they are asked. */
export const SCREENING_QUESTIONS_BY_LEVEL: Readonly<
  Record<ScreeningQuestionLevel, readonly ScreeningQuestionCode[]>
> = {
  TAXPAYER: FISCAL_OBLIGATION_SCREENING_QUESTIONS.filter(
    (question) => question.level === 'TAXPAYER',
  ).map((question) => question.code),
  ENTITY: FISCAL_OBLIGATION_SCREENING_QUESTIONS.filter(
    (question) => question.level === 'ENTITY',
  ).map((question) => question.code),
};

/** Whether a question is about the company (its Taxpayer) or about one establishment. */
export function screeningQuestionLevel(code: ScreeningQuestionCode): ScreeningQuestionLevel {
  return SCREENING_QUESTIONS_BY_LEVEL.TAXPAYER.includes(code) ? 'TAXPAYER' : 'ENTITY';
}

/** Answers by question; a question that is not in the map is unanswered. */
export type ScreeningAnswers = Partial<Record<ScreeningQuestionCode, ObligationScreeningAnswer>>;

export interface ScreeningQuestionState {
  code: ScreeningQuestionCode;
  level: ScreeningQuestionLevel;
  group: ScreeningQuestionGroup;
  scopeType: ObligationScopeType;
  /** The Taxpayer or the establishment the answer is stored for; null without a Taxpayer. */
  scopeId: string | null;
  answer: ObligationScreeningAnswer | null;
  answeredAt: string | null;
  answeredByUserId: string | null;
  editable: boolean;
  readOnlyReason: ObligationReadOnlyReason | null;
}

export interface EntityScreeningResponse {
  entityId: string;
  questions: ScreeningQuestionState[];
  /** How many questions have no answer yet. */
  unansweredCount: number;
}

export interface AnswerScreeningRequest {
  answers: { questionCode: ScreeningQuestionCode; answer: ObligationScreeningAnswer }[];
}

/** Why the ERP suggests what it suggests. Stable codes; the texts are the ones the accountant sees. */
export const OBLIGATION_SUGGESTION_REASON_CODES = [
  'SPECIAL_REGIME_NO_COVERAGE',
  'PROFILE_MISSING',
  'QUESTIONS_UNANSWERED',
  'NOT_COVERED_BY_SCREENING',
  'REGIME_SIMPLES',
  'REGIME_NOT_SIMPLES',
  'MIT_OUTSIDE_SIMPLES',
  'MIT_SIMPLES_NEEDS_DECISION',
  'TREATMENT_NOT_REGISTERED',
  'TREATMENT_REGULAR_COLLECTION',
  'TREATMENT_INSIDE_DAS',
  'TREATMENT_REGIME_MISMATCH',
  'TREATMENT_OUT_OF_SCOPE',
  'PAYROLL',
  'WITHHOLDINGS',
  'DIVIDENDS',
  'NO_TRIGGER_DECLARED',
  'ECD_REQUIRED_BY_REGIME',
  'ECD_EXEMPTION_NEEDS_FACTS',
  'CORPORATE_EVENT_ANTICIPATED',
  'ECF_REQUIRED_BY_REGIME',
  'ECF_SIMPLES_DISPENSED',
  'MIXED_SCENARIO',
  'ISS_DF_NO_COVERAGE',
  'ISS_ORDINARY_SERVICES',
  'GOODS_NO_COVERAGE',
  'ICMS_IPI_NO_COVERAGE',
  'NO_GOODS_OR_ICMS_DECLARED',
  'SERVICES_IMPORT_NO_COVERAGE',
  'SAAS_IMPORT_NO_COVERAGE',
  'NO_SERVICES_IMPORT_DECLARED',
  'SERVICES_EXPORT_NO_COVERAGE',
  'SAAS_EXPORT_NO_COVERAGE',
  'NO_SERVICES_EXPORT_DECLARED',
] as const;
export type ObligationSuggestionReasonCode = (typeof OBLIGATION_SUGGESTION_REASON_CODES)[number];

export interface ObligationSuggestionReason {
  code: ObligationSuggestionReasonCode;
  ptBR: string;
  en: string;
}

/** The legal basis and evidence a suggested waiver carries, so accepting it needs no typing. */
export interface ObligationSuggestionBasis {
  basisCode: string;
  basisDescription: string;
  evidenceCode: string;
  evidenceDescription: string;
}

/** What the suggestion function reads of the Taxpayer profile at the day (never raw CNAE). */
export interface ObligationSuggestionProfile {
  companyTaxRegime: CompanyTaxRegime;
  consumptionTreatments: {
    CBS: { treatmentId: string; treatment: ConsumptionTaxTreatment } | null;
    IBS: { treatmentId: string; treatment: ConsumptionTaxTreatment } | null;
  };
}

export interface ObligationSuggestion {
  obligationCode: string;
  state: ObligationApplicabilityState;
  reason: ObligationSuggestionReason;
  /** Set when `state` is `WAIVED_WITH_BASIS`. */
  basis: ObligationSuggestionBasis | null;
  /** The screening questions that still decide this obligation, when it waits for answers. */
  missingQuestions: ScreeningQuestionCode[];
}

/** A suggestion read for one establishment's tab: next to the decision in force and its conflict. */
export interface EntityObligationSuggestionItem {
  obligation: FiscalObligationCatalogItem;
  scopeType: ObligationScopeType;
  scopeId: string | null;
  editable: boolean;
  readOnlyReason: ObligationReadOnlyReason | null;
  suggestion: ObligationSuggestion;
  /** The decision in force at `asOf`, or null when none has been made. */
  current: ObligationApplicabilityDecision | null;
  /**
   * The suggestion can be accepted: it is a decision (not "pending"), the establishment may write
   * it, and it is not already the decision in force.
   */
  acceptable: boolean;
  /**
   * A decision in force that is not "pending" and differs from what the ERP now suggests (the
   * profile or the answers changed since): the accountant should review it.
   */
  conflict: boolean;
}

export type ObligationSuggestionProfileStatus =
  | { status: 'RESOLVED'; profile: ObligationSuggestionProfile }
  | { status: 'NO_TAXPAYER' }
  | { status: 'NO_FISCAL_HISTORY' };

export interface EntityObligationSuggestionsResponse {
  entityId: string;
  asOf: string;
  profile: ObligationSuggestionProfileStatus;
  unansweredQuestions: ScreeningQuestionCode[];
  items: EntityObligationSuggestionItem[];
  /** Decisions to review: how many items are in conflict. */
  conflictCount: number;
  /** How many suggestions can be accepted now. */
  acceptableCount: number;
}

export interface AcceptObligationSuggestionsRequest {
  /** The first day the accepted decisions hold (default: the day the suggestions were read for). */
  validFrom?: string;
  /** What the accountant saw: each is accepted only if the ERP still suggests this state. */
  suggestions: { obligationCode: string; state: ObligationApplicabilityState }[];
  /**
   * Day the suggestions are computed for when `validFrom` is not given (default: today, or the
   * calendar start when later). With `validFrom`, they are checked on that day.
   */
  asOf?: string;
}

export type AcceptedSuggestionResult =
  | { obligationCode: string; accepted: true; decision: ObligationApplicabilityDecision }
  | { obligationCode: string; accepted: false; errorCode: string; message: string };

export interface AcceptObligationSuggestionsResponse {
  results: AcceptedSuggestionResult[];
  acceptedCount: number;
}
